// src/components/trackinginfo/components/TrackingInfoTable.jsx
// ============================================================================
// BLOCK TI.TABLE.0 — Tracking Info Table Component (with tracking hover popup)
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import axios from '../../../axiosClient.js';

// ============================================================================
// BLOCK TI.TABLE.PAGINATION.0 — Client-side pagination (Commissions-style)
// Notes:
//  - Keeps existing vertical scroll via TableContainer maxHeight
//  - Paginates the *rows* array client-side (no API changes)
// ============================================================================
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250];

function fmtDate(v) {
  if (!v) {
    return '';
  }

  const raw = String(v).trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, yyyy, mm, dd] = m;
    return `${Number(mm)}/${dd}/${yyyy.slice(-2)}`;
  }

  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  return `${d.getUTCMonth() + 1}/${String(d.getUTCDate()).padStart(2, '0')}/${String(
    d.getUTCFullYear()
  ).slice(-2)}`;
}

// ============================================================================
// BLOCK TI.TABLE.TRACKLINK.0 — Tracking link helpers (UPS/FedEx)
// ============================================================================

function inferCarrier({ trackingNo, serviceType, serviceTypeDesc }) {
  const t = String(trackingNo || '')
    .trim()
    .toUpperCase();
  const st = String(serviceType || '').toUpperCase();
  const sd = String(serviceTypeDesc || '').toUpperCase();

  // Strong signals
  if (t.startsWith('1Z')) return 'UPS';

  // FedEx is often numeric (12/15/20/22 digits are common)
  const digitsOnly = /^\d+$/.test(t);
  if (digitsOnly && [12, 15, 20, 22].includes(t.length)) return 'FEDEX';

  // Fallback: look at service fields
  if (st.includes('UPS') || sd.includes('UPS')) return 'UPS';
  if (st.includes('FEDEX') || sd.includes('FEDEX')) return 'FEDEX';

  return null;
}

function buildTrackingUrl({ trackingNo, serviceType, serviceTypeDesc }) {
  const t = String(trackingNo || '').trim();
  if (!t) return null;

  const carrier = inferCarrier({ trackingNo: t, serviceType, serviceTypeDesc });

  if (carrier === 'UPS') {
    return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(t)}`;
  }
  if (carrier === 'FEDEX') {
    return `https://www.fedex.com/fedextrack/?tracknumbers=${encodeURIComponent(t)}`;
  }

  // Unknown carrier (don’t link)
  return null;
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) {
    return '';
  }
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function formatApiDateTimeExact(value) {
  if (!value) {
    return { date: '-', time: '-' };
  }

  const raw = String(value).trim();
  const m = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/
  );

  if (m) {
    const [, yyyy, mm, dd, hh, min] = m;
    return {
      date: `${Number(mm)}/${dd}/${yyyy.slice(-2)}`,
      time: hh && min ? `${hh}:${min}` : '-',
    };
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return { date: '-', time: '-' };
  }

  const iso = d.toISOString();
  const isoMatch = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!isoMatch) {
    return { date: '-', time: '-' };
  }

  const [, yyyy, mm, dd, hh, min] = isoMatch;
  return {
    date: `${Number(mm)}/${dd}/${yyyy.slice(-2)}`,
    time: `${hh}:${min}`,
  };
}

function formatLocation(loc) {
  if (!loc) {
    return '-';
  }

  let normalizedLocation = loc;
  if (typeof normalizedLocation === 'string') {
    try {
      normalizedLocation = JSON.parse(normalizedLocation);
    } catch {
      // Keep plain text location if value is not JSON.
      return normalizedLocation || '-';
    }
  }

  const city = normalizedLocation.city || normalizedLocation.City || '';
  const state =
    normalizedLocation.stateOrProvinceCode ||
    normalizedLocation.state ||
    normalizedLocation.State ||
    '';
  const country =
    normalizedLocation.countryCode ||
    normalizedLocation.country ||
    normalizedLocation.Country ||
    '';
  const parts = [city, state].filter(Boolean).join(', ');
  return [parts, country].filter(Boolean).join(' ');
}

function pickFirstTrackingNumber(trackingIdRaw) {
  const raw = String(trackingIdRaw || '').trim();
  if (!raw) {
    return '';
  }
  return raw.split(/[\s,;|]+/).filter(Boolean)[0] || '';
}

// ============================================================================
// BLOCK TI.TABLE.TRACKHOVER.0 — TrackingStatusHover (for tracking info rows)
// Wrap any child UI and show a tooltip that loads tracking status on hover.
// Uses existing endpoint: GET /api/shipping/track?trackingNumber=...
// ============================================================================
function TrackingStatusHover({ trackingNumberRaw, carrierHint, children }) {
  const NON_DELIVERED_REFRESH_MS = 60 * 1000;

  const trackingNumber = React.useMemo(
    () => pickFirstTrackingNumber(trackingNumberRaw),
    [trackingNumberRaw]
  );

  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [lastFetchedAt, setLastFetchedAt] = React.useState(0);

  const fetchOnce = React.useCallback(async () => {
    if (!trackingNumber) {
      return;
    }
    if (loading) {
      return;
    }

    const hasData = Boolean(data);
    const hasError = Boolean(error);
    const isDelivered = Boolean(data?.delivered);
    const isFresh = Date.now() - Number(lastFetchedAt || 0) < NON_DELIVERED_REFRESH_MS;

    // Keep delivered rows sticky for this row instance, but refresh in-transit/not-found
    // rows periodically so hover reflects current carrier state.
    if ((isDelivered && hasData) || (!hasError && hasData && isFresh)) {
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get('/api/shipping/track', {
        params: {
          trackingNumber,
          ...(carrierHint ? { carrier: carrierHint } : {}),
          ...(hasData && !isDelivered ? { refresh: '1' } : {}),
        },
      });
      setData(res.data);
      setError(null);
      setLastFetchedAt(Date.now());
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Tracking lookup failed');
      setLastFetchedAt(Date.now());
    } finally {
      setLoading(false);
    }
  }, [NON_DELIVERED_REFRESH_MS, carrierHint, data, error, lastFetchedAt, loading, trackingNumber]);

  const tooltipContent = React.useMemo(() => {
    if (!trackingNumber) {
      return <Typography variant="body2">No tracking number</Typography>;
    }

    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading status…</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Typography variant="body2" sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
          {error}
        </Typography>
      );
    }

    if (!data?.ok) {
      const msg =
        data?.errorMessage ||
        data?.error ||
        data?.message ||
        (data?.errorCode ? `Tracking unavailable (${data.errorCode})` : null) ||
        'No tracking status available.';
      return (
        <Typography variant="body2" sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
          {msg}
        </Typography>
      );
    }

    const delivered = Boolean(data.delivered);
    const eta = data.eta || data.etaDate || null;
    const lastLoc = data.lastLocation || null;
    const statusText = data.statusText || data.status || null;
    const lastEventAt = data.lastEventAt || data.last_event_at || null;

    const deliveredAt = data.deliveredAt || null;
    const signedForBy = data.signedForBy || data.signed_by || null;

    const deliveredFmt = formatApiDateTimeExact(deliveredAt);
    const etaFmt = formatApiDateTimeExact(eta);
    const lastEventFmt = formatApiDateTimeExact(lastEventAt);

    const hasTrackingDetails = Boolean(statusText || eta || lastLoc || lastEventAt || delivered);
    if (!hasTrackingDetails) {
      return (
        <Typography variant="body2" sx={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
          Tracking is active, but no scan details are available yet.
        </Typography>
      );
    }

    if (delivered) {
      return (
        <Box sx={{ maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Delivered
          </Typography>
          <Typography variant="body2">Date: {deliveredFmt.date}</Typography>
          <Typography variant="body2">Time: {deliveredFmt.time}</Typography>
          <Typography variant="body2">Name: {signedForBy || '-'}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxWidth: 320 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          In Transit
        </Typography>
        <Typography variant="body2">Status: {statusText || '-'}</Typography>
        <Typography variant="body2">
          Last Scan: {lastEventAt ? `${lastEventFmt.date} ${lastEventFmt.time}` : '-'}
        </Typography>
        <Typography variant="body2">Est DeliverDate: {eta ? etaFmt.date : '-'}</Typography>
        <Typography variant="body2">Current Location: {formatLocation(lastLoc)}</Typography>
      </Box>
    );
  }, [data, error, loading, trackingNumber]);

  return (
    <Tooltip arrow placement="top" onOpen={fetchOnce} title={tooltipContent}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>{children}</Box>
    </Tooltip>
  );
}

TrackingStatusHover.propTypes = {
  trackingNumberRaw: PropTypes.string,
  carrierHint: PropTypes.string,
  children: PropTypes.node.isRequired,
};

function TrackingInfoTable({ rows }) {
  // ==========================================================================
  // BLOCK TI.TABLE.PAGINATION.1 — State
  // ==========================================================================
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(50);

  const safeRows = React.useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const total = safeRows.length;

  // Reset page when dataset size changes (prevents blank page after filters)
  React.useEffect(() => {
    setPage(0);
  }, [total]);

  const pageRows = React.useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return safeRows.slice(start, end);
  }, [safeRows, page, rowsPerPage]);

  return (
    <Paper elevation={2} sx={{ p: 1 }}>
      <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ShipDate</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>PO</TableCell>
              <TableCell>TrackingNo</TableCell>
              <TableCell>DeliveryDate</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>ZipCode</TableCell>
              <TableCell>SO</TableCell>
              <TableCell>ServiceType</TableCell>
              <TableCell align="right">PublishedRate</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageRows.map((r, idx) => {
              const trackingRaw = r?.MstrTrackingNo ?? r?.TrackingNo ?? '';
              // make key extra-safe in case OrderNo/tracking are blank/duplicated
              const key = String(r?.id ?? `${r?.OrderNo}-${trackingRaw}-${idx}`);

              return (
                <TableRow key={key}>
                  <TableCell>{fmtDate(r?.ShipDate)}</TableCell>
                  <TableCell>{r?.Company ?? ''}</TableCell>
                  <TableCell>{r?.CustomerRef ?? ''}</TableCell>

                  {/* ============================================================
                   BLOCK TI.TABLE.TRACKCELL.2 — Tracking hover popup + chip bubble + click-out
                    - Bubble (Chip) is readable in dark mode
                    - Hover: loads status tooltip
                    - Click: opens carrier tracking page (UPS/FedEx)
                    ============================================================ */}
                  <TableCell>
                    {trackingRaw ? (
                      <TrackingStatusHover
                        trackingNumberRaw={String(trackingRaw)}
                        carrierHint={
                          inferCarrier({
                            trackingNo: pickFirstTrackingNumber(trackingRaw),
                            serviceType: r?.ServiceType,
                            serviceTypeDesc: r?.ServiceTypeDesc,
                          }) || undefined
                        }
                      >
                        <Chip
                          size="small"
                          label={String(trackingRaw)}
                          clickable
                          component="a"
                          href={
                            buildTrackingUrl({
                              trackingNo: pickFirstTrackingNumber(trackingRaw),
                              serviceType: r?.ServiceType,
                              serviceTypeDesc: r?.ServiceTypeDesc,
                            }) || undefined
                          }
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => {
                            const href = buildTrackingUrl({
                              trackingNo: pickFirstTrackingNumber(trackingRaw),
                              serviceType: r?.ServiceType,
                              serviceTypeDesc: r?.ServiceTypeDesc,
                            });

                            if (!href) {
                              e.preventDefault();
                              e.stopPropagation();
                              return;
                            }

                            e.stopPropagation();
                          }}
                          sx={{
                            maxWidth: 260,
                            '& .MuiChip-label': {
                              maxWidth: 240,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </TrackingStatusHover>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{fmtDate(r?.DeliveryDate)}</TableCell>
                  <TableCell>{r?.City ?? ''}</TableCell>
                  <TableCell>{r?.State ?? ''}</TableCell>
                  <TableCell>{r?.ZipCode ?? ''}</TableCell>
                  <TableCell>{r?.OrderNo ?? ''}</TableCell>
                  <TableCell>{r?.ServiceType ?? r?.ServiceTypeDesc ?? ''}</TableCell>
                  <TableCell align="right">{fmtMoney(r?.PCharge)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        showFirstButton
        showLastButton
      />
    </Paper>
  );
}

TrackingInfoTable.propTypes = {
  rows: PropTypes.array,
};

export default React.memo(TrackingInfoTable);
